import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { normalizeWord, normalizeClue } from '@/lib/textUtils';

export interface ClueSuggestion {
  id: number;
  word: string;
  word_upper: string;
  clue: string;
  status: 'pending' | 'approved' | 'rejected';
  admin_note: string | null;
  created_at: string;
  reviewed_at: string | null;
  submitted_by?: string;
  word_exists?: boolean;
}

export function useClueSuggestions() {
  const [loading, setLoading] = useState(false);
  const [mySuggestions, setMySuggestions] = useState<ClueSuggestion[]>([]);
  const [pendingSuggestions, setPendingSuggestions] = useState<ClueSuggestion[]>([]);

  const submitClue = async (word: string, clue: string) => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Du måste vara inloggad');

      const upperWord = word.toUpperCase();

      // Check if word exists - if so, check for duplicate clue
      const { data: existingWord } = await supabase
        .from('words')
        .select('id')
        .eq('word_upper', upperWord)
        .maybeSingle();

      if (existingWord) {
        // Check if this exact clue already exists
        const { data: existingClue } = await supabase
          .from('clues')
          .select('id')
          .eq('word_id', existingWord.id)
          .eq('clue_upper', clue.toUpperCase())
          .maybeSingle();

        if (existingClue) {
          return { error: 'Denna ledtråd finns redan för detta ord.', message: null };
        }
      }

      // Check if suggestion already pending
      const { data: pendingSuggestion } = await supabase
        .from('clue_suggestions')
        .select('id')
        .eq('word_upper', upperWord)
        .eq('clue', clue)
        .eq('status', 'pending')
        .maybeSingle();

      if (pendingSuggestion) {
        return { error: 'Detta förslag väntar redan på granskning.', message: null };
      }

      const { error } = await supabase
        .from('clue_suggestions')
        .insert({
          user_id: user.id,
          word: word.toUpperCase(),
          word_upper: upperWord,
          clue,
          status: 'pending',
        });

      if (error) throw error;
      return { error: null, message: 'Ledtrådsförslag skickat!' };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Kunde inte skicka förslag';
      return { error: message, message: null };
    } finally {
      setLoading(false);
    }
  };

  const fetchMySuggestions = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Ej inloggad');

      const { data, error } = await supabase
        .from('clue_suggestions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMySuggestions((data || []) as ClueSuggestion[]);
      return { error: null };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Kunde inte hämta förslag';
      return { error: message };
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingSuggestions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('clue_suggestions')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Fetch display names
      const userIds = [...new Set((data || []).map(s => s.user_id).filter(Boolean))];
      let profileMap: Record<string, string> = {};
      
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, display_name')
          .in('id', userIds);
        
        profileMap = (profiles || []).reduce((acc, p) => {
          acc[p.id] = p.display_name;
          return acc;
        }, {} as Record<string, string>);
      }
      
      // Check which words exist
      const wordUppers = [...new Set((data || []).map(s => s.word_upper))];
      let existingWords: Set<string> = new Set();
      
      if (wordUppers.length > 0) {
        const { data: words } = await supabase
          .from('words')
          .select('word_upper')
          .in('word_upper', wordUppers);
        
        existingWords = new Set((words || []).map(w => w.word_upper));
      }
      
      const suggestions = (data || []).map(s => ({
        ...s,
        submitted_by: s.user_id ? profileMap[s.user_id] || 'Okänd' : 'Okänd',
        word_exists: existingWords.has(s.word_upper),
      }));
      
      setPendingSuggestions(suggestions as ClueSuggestion[]);
      return { error: null };
    } catch (error) {
      console.error('Error fetching pending clue suggestions:', error);
      const message = error instanceof Error ? error.message : 'Kunde inte hämta förslag';
      return { error: message };
    } finally {
      setLoading(false);
    }
  };

  const reviewSuggestion = async (
    suggestionId: number, 
    reviewAction: 'approve' | 'reject', 
    editedClue?: string
  ) => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Ej inloggad');

      const newStatus = reviewAction === 'approve' ? 'approved' : 'rejected';

      // Get the suggestion
      const { data: suggestion } = await supabase
        .from('clue_suggestions')
        .select('word, word_upper, clue')
        .eq('id', suggestionId)
        .single();

      if (!suggestion) throw new Error('Förslag hittades inte');

      const finalClue = editedClue !== undefined ? editedClue : suggestion.clue;

      // Update suggestion
      const { error } = await supabase
        .from('clue_suggestions')
        .update({
          clue: finalClue,
          status: newStatus,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', suggestionId);

      if (error) throw error;

      // If approved, add word (if needed) and clue to database in BOTH directions
      if (reviewAction === 'approve') {
        // Direction 1: Word -> Clue (e.g., HUND -> "Djur")
        let { data: existingWord1 } = await supabase
          .from('words')
          .select('id')
          .eq('word_upper', suggestion.word_upper)
          .maybeSingle();

        if (!existingWord1) {
          const { data: newWord, error: wordError } = await supabase
            .from('words')
            .insert({
              word: normalizeWord(suggestion.word),
              word_upper: suggestion.word_upper,
              length: suggestion.word.length,
            })
            .select('id')
            .single();

          if (wordError) throw wordError;
          existingWord1 = newWord;
        }

        if (existingWord1) {
          await supabase
            .from('clues')
            .insert({
              word_id: existingWord1.id,
              clue: normalizeClue(finalClue),
              clue_upper: finalClue.toUpperCase(),
            });
        }

        // Direction 2: Add each word in the clue as a separate word entry
        const wordAsClue = suggestion.word.trim();
        const clueWords = finalClue.trim().split(/\s+/).filter(w => w.length >= 2);

        for (const clueWord of clueWords) {
          const clueWordUpper = clueWord.toUpperCase().replace(/[^A-ZÅÄÖ]/g, '');
          if (!clueWordUpper || clueWordUpper.length < 2) continue;

          let { data: existingWord2 } = await supabase
            .from('words')
            .select('id')
            .eq('word_upper', clueWordUpper)
            .maybeSingle();

          if (!existingWord2) {
            const { data: newWord2, error: wordError2 } = await supabase
              .from('words')
              .insert({
                word: normalizeWord(clueWord.replace(/[^a-zA-ZåäöÅÄÖ]/g, '')),
                word_upper: clueWordUpper,
                length: clueWordUpper.length,
              })
              .select('id')
              .single();
            if (!wordError2) existingWord2 = newWord2;
          }

          if (existingWord2) {
            // Check if this clue already exists for this word
            const { data: existingClue } = await supabase
              .from('clues')
              .select('id')
              .eq('word_id', existingWord2.id)
              .eq('clue_upper', wordAsClue.toUpperCase())
              .maybeSingle();

            if (!existingClue) {
              await supabase
                .from('clues')
                .insert({
                  word_id: existingWord2.id,
                  clue: normalizeClue(wordAsClue),
                  clue_upper: wordAsClue.toUpperCase(),
                });
            }
          }
        }
      }

      setPendingSuggestions(prev => prev.filter(s => s.id !== suggestionId));
      return { error: null };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Kunde inte granska förslag';
      return { error: message };
    } finally {
      setLoading(false);
    }
  };

  const checkWordExists = useCallback(async (word: string): Promise<boolean> => {
    try {
      const { data } = await supabase
        .from('words')
        .select('id')
        .eq('word_upper', word.toUpperCase())
        .maybeSingle();
      return !!data;
    } catch {
      return false;
    }
  }, []);

  return {
    loading,
    mySuggestions,
    pendingSuggestions,
    submitClue,
    checkWordExists,
    fetchMySuggestions,
    fetchPendingSuggestions,
    reviewSuggestion,
  };
}
